//stock-and-audit-page.tsx

import {
  Box,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { MobileDatePicker } from "@mui/x-date-pickers";
import { SelectChangeEvent } from "@mui/material/Select";
import { useState } from "react";
import { useEffect } from "react";
import { seedsApi } from "../services/api/seeds";
import { Seed, SeedStockHistoryRecord, SeedUseRecord } from "../models/seed";
import { DateTime } from "luxon";
import { useContext } from "react";
import { UserContext } from "../components/app-routes";
import { formatCurrency } from "./currency";
import { GridColDef } from "@mui/x-data-grid";
import { StripedDataGrid } from "../components/striped-data-grid";
import { GridToolbar } from "@mui/x-data-grid";
import { pkgLotsApi } from "../services/api/pkg-lots";
import {
  Pkg,
  PkgStockSummary,
  PkgUseRecord,
  PkgStockHistoryRow,
} from "../models/pkg";
import { pkgsApi } from "../services/api/pkgs";

export default function StockAndAuditPage() {
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedSeedLotId, setSelectedSeedLotId] = useState("all");
  const [selectedPkgLotId, setSelectedPkgLotId] = useState("all");

  const [seeds, setSeeds] = useState<Seed[]>([]);
  const [pkgs, setPkgs] = useState<Pkg[]>([]);
  const [seedUseRecords, setSeedUseRecords] = useState<SeedUseRecord[]>([]);
  const [pkgUseRecords, setPkgUseRecords] = useState<PkgUseRecord[]>([]);

  const [pkgStockSummary, setPkgStockSummary] = useState<PkgStockSummary[]>([]);
  const [fromDate, setFromDate] = useState<DateTime | null>(
    DateTime.now().startOf("year"),
  );
  const [seedAuditOpen, setSeedAuditOpen] = useState(false);
  const [seedAuditCountedStock, setSeedAuditCountedStock] = useState("");
  const [seedAuditNotes, setSeedAuditNotes] = useState("");
  const [seedAuditConfirmOpen, setSeedAuditConfirmOpen] = useState(false);
  const [editingSeedAuditTransaction, setEditingSeedAuditTransaction] =
    useState<any>(null);

  const [pkgAuditOpen, setPkgAuditOpen] = useState(false);
  const [pkgAuditConfirmOpen, setPkgAuditConfirmOpen] = useState(false);
  const [pkgAuditCountedStock, setPkgAuditCountedStock] = useState("");
  const [pkgAuditNotes, setPkgAuditNotes] = useState("");
  const [editingPkgAuditTransaction, setEditingPkgAuditTransaction] =
    useState<any>(null);

  enum DateRange {
    Last30Days = "last30days",
    ThisMonth = "thisMonth",
    LastMonth = "lastMonth",
    ThisYear = "thisYear",
    Custom = "custom",
  }

  const [dateRange, setDateRange] = useState<DateRange>(DateRange.ThisYear);

  const [toDate, setToDate] = useState<DateTime | null>(DateTime.now());

  const handleChangeDateRange = (event: SelectChangeEvent<DateRange>) => {
    const value = event.target.value as DateRange;
    const now = DateTime.now();

    setDateRange(value);

    if (value === DateRange.Last30Days) {
      setFromDate(now.minus({ days: 30 }));
      setToDate(now);
    }

    if (value === DateRange.ThisMonth) {
      setFromDate(now.startOf("month"));
      setToDate(now.endOf("month"));
    }

    if (value === DateRange.LastMonth) {
      setFromDate(now.minus({ months: 1 }).startOf("month"));
      setToDate(now.minus({ months: 1 }).endOf("month"));
    }

    if (value === DateRange.ThisYear) {
      setFromDate(now.startOf("year"));
      setToDate(now.endOf("year"));
    }
  };

  const selectedLot =
    selectedSeedLotId === "all"
      ? null
      : seeds
          .flatMap((seed) =>
            (seed.lots ?? []).map((lot) => ({
              seed,
              lot,
            })),
          )
          .find((x) => x.lot.id.toString() === selectedSeedLotId);

  const displayedSeedUseRecords =
    selectedSeedLotId === "all"
      ? (seedUseRecords ?? [])
      : (seedUseRecords ?? []).filter(
          (record) => record.seedLot === selectedLot?.lot.number,
        );

  const filteredSeedUseRecords = (displayedSeedUseRecords ?? []).filter(
    (record) => {
      const usedDate = DateTime.fromISO(record.dateUsed);

      if (fromDate && usedDate < fromDate.startOf("day")) {
        return false;
      }

      if (toDate && usedDate > toDate.endOf("day")) {
        return false;
      }

      return true;
    },
  );

  const [seedStockHistoryRows, setSeedStockHistoryRows] = useState<
    SeedStockHistoryRecord[]
  >([]);

  const displayedSeedStockHistoryRows =
    selectedSeedLotId === "all"
      ? seedStockHistoryRows
      : seedStockHistoryRows.filter(
          (row) => row.seedLotId?.toString() === selectedSeedLotId,
        );

  const seedUsedKg = (displayedSeedStockHistoryRows ?? [])
    .filter((row) => row.transaction !== "received")
    .reduce((total, row) => total + Math.abs(row.adjustment || 0), 0);

  const activeSeedLots = (seeds ?? []).flatMap((seed) =>
    (seed.lots ?? [])
      .filter((lot) => lot.archivedAt === null)
      .map((lot) => ({
        seed,
        lot,
      })),
  );

  const activePkgLots = (pkgs ?? []).flatMap((pkg) =>
    (pkg.lots ?? [])
      .filter((lot) => lot.archivedAt === null)
      .map((lot) => ({
        pkg,
        lot,
      })),
  );

  const selectedPkgLot =
    selectedPkgLotId === "all"
      ? null
      : pkgs
          .flatMap((pkg) =>
            (pkg.lots ?? []).map((lot) => ({
              pkg,
              lot,
            })),
          )
          .find((x) => x.lot.id.toString() === selectedPkgLotId);

  const displayedPkgStockSummary =
    selectedPkgLotId === "all"
      ? (pkgStockSummary ?? [])
      : (pkgStockSummary ?? []).filter(
          (record) =>
            record.pkgLot === selectedPkgLot?.lot.number &&
            record.pkgName === selectedPkgLot?.pkg.name,
        );

  const displayedPkgLots =
    selectedPkgLotId === "all"
      ? activePkgLots
      : activePkgLots.filter(
          ({ lot }) => lot.id.toString() === selectedPkgLotId,
        );

  const pkgOpeningStock = (displayedPkgLots ?? []).reduce(
    (total, { lot }) => total + (lot.openingStock || lot.qtyReceived || 0),
    0,
  );

  const [pkgStockHistoryRows, setPkgStockHistoryRows] = useState<
    PkgStockHistoryRow[]
  >([]);

  const displayedPkgStockHistoryRows =
    selectedPkgLotId === "all"
      ? pkgStockHistoryRows
      : pkgStockHistoryRows.filter(
          (row) => row.pkgLotId?.toString() === selectedPkgLotId,
        );

  const pkgUsed = displayedPkgStockHistoryRows.reduce(
    (total, row) =>
      row.transaction === "usage" || row.transaction === "audit_adjustment"
        ? total + Math.abs(row.adjustment || 0)
        : total,
    0,
  );

  const pkgCurrentStock = displayedPkgStockSummary.reduce(
    (total, lot) => total + (lot.currentStock || 0),
    0,
  );

  const pkgInventoryValue = displayedPkgStockSummary.reduce(
    (total, summaryLot) => {
      const pkgLot = pkgs
        .flatMap((pkg) => pkg.lots)
        .find((lot) => lot.number === summaryLot.pkgLot);

      const openingStock = pkgLot?.openingStock || pkgLot?.qtyReceived;

      if (!pkgLot?.costOfLot || !openingStock || !summaryLot.currentStock) {
        return total;
      }

      return (
        total + pkgLot.costOfLot * (summaryLot.currentStock / openingStock)
      );
    },
    0,
  );

  const displayedSeedLots =
    selectedSeedLotId === "all"
      ? activeSeedLots
      : activeSeedLots.filter(
          ({ lot }) => lot.id.toString() === selectedSeedLotId,
        );

  const seedInventoryValue = displayedSeedLots.reduce((total, { lot }) => {
    const openingStockKg = lot.openingStockKg || lot.qtyReceivedKg;

    if (!lot.costOfLot || !openingStockKg || !lot.currentStockKg) {
      return total;
    }

    return total + lot.costOfLot * (lot.currentStockKg / openingStockKg);
  }, 0);

  const { grower } = useContext(UserContext);

  const openingStockKg = (displayedSeedLots ?? []).reduce(
    (total, { lot }) => total + (lot.openingStockKg || 0),
    0,
  );

  const seedInputRows = (displayedSeedLots ?? [])
    .filter(({ lot }) => lot.dateReceived && lot.qtyReceivedKg)
    .map(({ seed, lot }) => {
      const costPer100g =
        lot.costOfLot && lot.qtyReceivedKg
          ? lot.costOfLot / 100 / (lot.qtyReceivedKg * 10)
          : undefined;

      return {
        id: lot.id,

        seedName: seed.name,
        seedLot: lot.number,
        type: lot.isMix ? "Mix Created" : "Input",
        supplier: lot.supplier,
        invoiceNumber: lot.invoiceNumber,
        dateReceivedRaw: lot.dateReceived,
        dateReceived: lot.dateReceived
          ? DateTime.fromISO(lot.dateReceived).toLocaleString(
              DateTime.DATE_FULL,
            )
          : "",
        qtyReceivedKg: lot.qtyReceivedKg,
        costOfLot: lot.costOfLot ? (lot.costOfLot / 100).toFixed(2) : "",
        costPer100g: costPer100g ? costPer100g.toFixed(2) : "",
      };
    })
    .sort((a, b) =>
      (b.dateReceivedRaw || "").localeCompare(a.dateReceivedRaw || ""),
    );

  const filteredSeedInputRows = seedInputRows.filter((row) => {
    const receivedDate = DateTime.fromISO(row.dateReceivedRaw || "");

    if (fromDate && receivedDate < fromDate.startOf("day")) {
      return false;
    }

    if (toDate && receivedDate > toDate.endOf("day")) {
      return false;
    }

    return true;
  });

  const filteredSeedStockHistoryRows = displayedSeedStockHistoryRows.filter(
    (row) => {
      const rowDate = DateTime.fromISO(String(row.date).split(" ")[0]);

      if (fromDate && rowDate < fromDate.startOf("day")) {
        return false;
      }

      if (toDate && rowDate > toDate.endOf("day")) {
        return false;
      }

      return true;
    },
  );

  const filteredPkgStockHistoryRows = displayedPkgStockHistoryRows.filter(
    (row) => {
      const rowDate = DateTime.fromISO(String(row.date).split(" ")[0]);

      if (fromDate && rowDate < fromDate.startOf("day")) {
        return false;
      }

      if (toDate && rowDate > toDate.endOf("day")) {
        return false;
      }

      return true;
    },
  );

  const pkgInputRows = (displayedPkgLots ?? [])
    .filter(({ lot }) => lot.dateReceived && lot.qtyReceived)
    .map(({ pkg, lot }) => {
      const costPerPackage =
        lot.costOfLot && lot.qtyReceived
          ? lot.costOfLot / lot.qtyReceived
          : undefined;

      return {
        id: lot.id,
        dateReceivedRaw: lot.dateReceived,
        dateReceived: lot.dateReceived
          ? DateTime.fromISO(lot.dateReceived).toLocaleString(
              DateTime.DATE_FULL,
            )
          : "",
        pkgName: pkg.name,
        pkgLot: lot.number,
        supplier: lot.supplier,
        invoiceNumber: lot.invoiceNumber,
        qtyReceived: lot.qtyReceived,
        costOfLot: lot.costOfLot ? (lot.costOfLot / 100).toFixed(2) : "",
        costPerPackage: costPerPackage ? (costPerPackage / 100).toFixed(2) : "",
      };
    })
    .sort((a, b) =>
      (b.dateReceivedRaw || "").localeCompare(a.dateReceivedRaw || ""),
    );

  const filteredPkgInputRows = pkgInputRows.filter((row) => {
    const receivedDate = DateTime.fromISO(row.dateReceivedRaw || "");

    if (fromDate && receivedDate < fromDate.startOf("day")) {
      return false;
    }

    if (toDate && receivedDate > toDate.endOf("day")) {
      return false;
    }

    return true;
  });

  const displayedPkgUseRecords =
    selectedPkgLotId === "all"
      ? (pkgUseRecords ?? [])
      : (pkgUseRecords ?? []).filter(
          (record) => record.pkgLot === selectedPkgLot?.lot.number,
        );

  const filteredPkgUseRecords = (displayedPkgUseRecords ?? []).filter(
    (record) => {
      const usedDate = DateTime.fromISO(record.dateUsed);

      if (fromDate && usedDate < fromDate.startOf("day")) {
        return false;
      }

      if (toDate && usedDate > toDate.endOf("day")) {
        return false;
      }

      return true;
    },
  );

  useEffect(() => {
    seedsApi.getAll().then((data) => setSeeds(data ?? []));
    seedsApi.getUseRecords().then((data) => setSeedUseRecords(data ?? []));
    seedsApi
      .getStockHistory()
      .then((data) => setSeedStockHistoryRows(data ?? []));
    pkgsApi.getAll().then((data) => setPkgs(data ?? []));
    pkgLotsApi.getUseRecords().then((data) => setPkgUseRecords(data ?? []));
    pkgLotsApi.getStockSummary().then((data) => setPkgStockSummary(data ?? []));
    pkgLotsApi
      .getStockHistory()
      .then((data) => setPkgStockHistoryRows(data ?? []));
  }, []);

  if (!grower) {
    throw new Error("Invalid UserContext");
  }

  const currentStockKg = (displayedSeedLots ?? []).reduce(
    (total, { lot }) => total + (lot.currentStockKg || 0),
    0,
  );

  const seedAuditExpectedStockKg = editingSeedAuditTransaction
    ? editingSeedAuditTransaction.balance -
      editingSeedAuditTransaction.adjustment
    : currentStockKg;

  const seedUseColumns: GridColDef[] = [
    {
      field: "seedName",
      headerName: "Seed",
      flex: 1,
    },
    {
      field: "seedLot",
      headerName: "Seed Lot",
      flex: 1,
    },
    {
      field: "dateUsed",
      headerName: "Date Used",
      flex: 1,
      valueFormatter: (value) =>
        value
          ? DateTime.fromISO(value as string).toLocaleString(DateTime.DATE_FULL)
          : "",
    },
    {
      field: "traysSowed",
      headerName: "Trays Sowed",
      flex: 1,
    },
    {
      field: "seedPerTrayGrams",
      headerName: "Seed/Tray (g)",
      flex: 1,
      valueFormatter: (value) => (value != null ? `${value} g` : ""),
    },
    {
      field: "totalUsedKg",
      headerName: "Total Used",
      flex: 1,
      valueFormatter: (value) =>
        value != null ? `${(Number(value) * 1000).toFixed(0)} g` : "",
    },
    {
      field: "costOfUse",
      headerName: "Cost of Use",
      flex: 1,
      valueFormatter: (value) =>
        value != null ? formatCurrency(Number(value), grower.CountryISO) : "",
    },
    {
      field: "cropBatchNumber",
      headerName: "Crop Batch #",
      flex: 1,
    },
    {
      field: "soakingNotes",
      headerName: "Batch Soaking Notes",
      flex: 2,
    },
    {
      field: "sowingNotes",
      headerName: "Batch Sowing Notes",
      flex: 2,
    },
  ];

  const seedInputColumns: GridColDef[] = [
    {
      field: "dateReceived",
      headerName: "Date Received",
      flex: 1,
    },
    {
      field: "type",
      headerName: "Type",
      flex: 1,
    },
    {
      field: "seedName",
      headerName: "Seed",
      flex: 1,
    },
    {
      field: "seedLot",
      headerName: "Lot #",
      flex: 1,
    },
    {
      field: "supplier",
      headerName: "Supplier",
      flex: 1,
    },
    {
      field: "invoiceNumber",
      headerName: "Invoice #",
      flex: 1,
    },
    {
      field: "qtyReceivedKg",
      headerName: "Qty Received (kg)",
      flex: 1,
    },
    {
      field: "costOfLot",
      headerName: "Lot Cost",
      flex: 1,
    },
    {
      field: "costPer100g",
      headerName: "Cost / 100g",
      flex: 1,
    },
  ];

  const seedStockHistoryColumns: GridColDef[] = [
    {
      field: "actions",
      headerName: "",
      width: 70,
      sortable: false,
      filterable: false,
      renderCell: (params) =>
        params.row.transaction === "audit_adjustment" ? (
          <IconButton
            size="small"
            onClick={() => {
              setEditingSeedAuditTransaction(params.row);
              setSeedAuditCountedStock(String(params.row.balance));
              setSeedAuditNotes(params.row.notes ?? "");
              setSeedAuditOpen(true);
            }}
          >
            <EditIcon />
          </IconButton>
        ) : null,
    },
    {
      field: "date",
      headerName: "Date",
      flex: 1,
      valueFormatter: (value) => {
        if (!value) {
          return "";
        }

        const isoDate = String(value).split(" ")[0];

        return DateTime.fromISO(isoDate).isValid
          ? DateTime.fromISO(isoDate).toLocaleString(DateTime.DATE_FULL)
          : "";
      },
    },
    {
      field: "seedName",
      headerName: "Seed",
      flex: 1,
    },
    {
      field: "lot",
      headerName: "Lot #",
      flex: 1,
    },
    {
      field: "transaction",
      headerName: "Transaction",
      flex: 1,
      valueFormatter: (value) =>
        String(value || "")
          .replace(/_/g, " ")
          .replace(/\b\w/g, (letter) => letter.toUpperCase()),
    },
    {
      field: "reference",
      headerName: "Batch #/Invoice #",
      flex: 1,
    },
    {
      field: "adjustment",
      headerName: "Adjustment",
      flex: 1,
      valueFormatter: (value) => {
        const amount = Number(value);

        if (Number.isNaN(amount)) {
          return "";
        }

        return `${amount > 0 ? "+" : ""}${amount.toFixed(3)} kg`;
      },
    },
    {
      field: "balance",
      headerName: "Balance",
      flex: 1,
      valueFormatter: (value) => {
        const amount = Number(value);

        if (Number.isNaN(amount)) {
          return "";
        }

        return `${amount > 0 ? "+" : ""}${amount.toFixed(3)} kg`;
      },
    },
    {
      field: "notes",
      headerName: "Notes",
      flex: 2,
    },
  ];

  const pkgUseColumns: GridColDef[] = [
    {
      field: "pkgName",
      headerName: "Package",
      flex: 1,
    },
    {
      field: "pkgLot",
      headerName: "Package Lot",
      flex: 1,
    },
    {
      field: "dateUsed",
      headerName: "Date Used",
      flex: 1,
      valueFormatter: (value) =>
        value
          ? DateTime.fromISO(value as string).toLocaleString(DateTime.DATE_FULL)
          : "",
    },
    {
      field: "quantityUsed",
      headerName: "Qty Used",
      flex: 1,
    },
    {
      field: "product",
      headerName: "Product",
      flex: 1,
    },
    {
      field: "productSize",
      headerName: "Product Size",
      flex: 1,
    },
    {
      field: "batchOrderInfo",
      headerName: "Order",
      flex: 2,
    },
    {
      field: "packagingNotes",
      headerName: "Packaging Notes",
      flex: 2,
    },
  ];

  const pkgInputColumns: GridColDef[] = [
    {
      field: "dateReceived",
      headerName: "Date Received",
      flex: 1,
    },
    {
      field: "pkgName",
      headerName: "Package",
      flex: 1,
    },
    {
      field: "pkgLot",
      headerName: "Lot #",
      flex: 1,
    },
    {
      field: "supplier",
      headerName: "Supplier",
      flex: 1,
    },
    {
      field: "invoiceNumber",
      headerName: "Invoice #",
      flex: 1,
    },
    {
      field: "qtyReceived",
      headerName: "Qty Received",
      flex: 1,
    },
    {
      field: "costOfLot",
      headerName: "Lot Cost",
      flex: 1,
    },
    {
      field: "costPerPackage",
      headerName: "Cost/Package",
      flex: 1,
    },
  ];

  const pkgStockHistoryColumns: GridColDef[] = [
    {
      field: "actions",
      headerName: "",
      width: 70,
      sortable: false,
      filterable: false,
      renderCell: (params) =>
        params.row.transaction === "audit_adjustment" ? (
          <IconButton
            size="small"
            onClick={() => {
              setEditingPkgAuditTransaction(params.row);
              setPkgAuditCountedStock(String(params.row.balance));
              setPkgAuditNotes(params.row.notes ?? "");
              setPkgAuditOpen(true);
            }}
          >
            <EditIcon />
          </IconButton>
        ) : null,
    },
    {
      field: "date",
      headerName: "Date",
      flex: 1,
      valueFormatter: (value) => {
        if (!value) {
          return "";
        }

        const isoDate = String(value).split(" ")[0];

        return DateTime.fromISO(isoDate).isValid
          ? DateTime.fromISO(isoDate).toLocaleString(DateTime.DATE_FULL)
          : "";
      },
    },
    {
      field: "pkgName",
      headerName: "Package",
      flex: 1,
    },
    {
      field: "lot",
      headerName: "Lot #",
      flex: 1,
    },
    {
      field: "transaction",
      headerName: "Transaction",
      flex: 1,
      valueFormatter: (value) =>
        String(value || "")
          .replace(/_/g, " ")
          .replace(/\b\w/g, (letter) => letter.toUpperCase()),
    },
    {
      field: "reference",
      headerName: "Order #/Invoice #",
      flex: 1,
    },
    {
      field: "adjustment",
      headerName: "Adjustment",
      flex: 1,
      valueFormatter: (value) => {
        const amount = Number(value);
        if (Number.isNaN(amount)) {
          return "";
        }
        return `${amount > 0 ? "+" : ""}${amount.toFixed(0)}`;
      },
    },
    {
      field: "balance",
      headerName: "Balance",
      flex: 1,
    },
    {
      field: "notes",
      headerName: "Notes",
      flex: 2,
    },
  ];

  const soilUseColumns: GridColDef[] = [
    {
      field: "dateUsed",
      headerName: "Date Used",
      flex: 1,
    },
    {
      field: "soilLot",
      headerName: "Soil Lot",
      flex: 1,
    },
    {
      field: "quantityUsed",
      headerName: "Qty Used",
      flex: 1,
    },
    {
      field: "reference",
      headerName: "Task / Reference",
      flex: 1,
    },
    {
      field: "notes",
      headerName: "Notes",
      flex: 2,
    },
  ];

  const soilInputColumns: GridColDef[] = [
    {
      field: "dateReceived",
      headerName: "Date Received",
      flex: 1,
    },
    {
      field: "soilName",
      headerName: "Soil",
      flex: 1,
    },
    {
      field: "soilLot",
      headerName: "Lot #",
      flex: 1,
    },
    {
      field: "supplier",
      headerName: "Supplier",
      flex: 1,
    },
    {
      field: "invoiceNumber",
      headerName: "Invoice #",
      flex: 1,
    },
    {
      field: "qtyReceived",
      headerName: "Qty Received",
      flex: 1,
    },
    {
      field: "costOfLot",
      headerName: "Lot Cost",
      flex: 1,
    },
    {
      field: "costPerUnit",
      headerName: "Cost / Unit",
      flex: 1,
    },
  ];

  const soilStockHistoryColumns: GridColDef[] = [
    {
      field: "date",
      headerName: "Date",
      flex: 1,
      valueFormatter: (value) => {
        if (!value) {
          return "";
        }

        const isoDate = String(value).split(" ")[0];

        return DateTime.fromISO(isoDate).isValid
          ? DateTime.fromISO(isoDate).toLocaleString(DateTime.DATE_FULL)
          : "";
      },
    },
    {
      field: "lot",
      headerName: "Lot #",
      flex: 1,
    },
    {
      field: "transaction",
      headerName: "Transaction",
      flex: 1,
      valueFormatter: (value) =>
        String(value || "")
          .replace(/_/g, " ")
          .replace(/\b\w/g, (letter) => letter.toUpperCase()),
    },
    {
      field: "reference",
      headerName: "Batch #/Invoice #",
      flex: 1,
    },
    {
      field: "adjustment",
      headerName: "Adjustment",
      flex: 1,
      valueFormatter: (value) => {
        const amount = Number(value);

        if (Number.isNaN(amount)) {
          return "";
        }

        return `${amount > 0 ? "+" : ""}${amount.toFixed(3)} kg`;
      },
    },
    {
      field: "balance",
      headerName: "Balance",
      flex: 1,
    },
    {
      field: "notes",
      headerName: "Notes",
      flex: 2,
    },
  ];

  const soilUseRows: any[] = [];
  const soilInputRows: any[] = [];
  const soilStockHistoryRows: any[] = [];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Stock and Audit
      </Typography>

      <Typography sx={{ mb: 2 }}>
        Track inventory, costs, and usage across all inputs.
      </Typography>

      <Tabs
        value={selectedTab}
        onChange={(_, value) => setSelectedTab(value)}
        sx={{ mb: 3 }}
      >
        <Tab label="Seeds" />
        <Tab label="Packaging" />
        <Tab label="Soil" />
      </Tabs>

      {selectedTab === 0 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Seed Lots
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "stretch",
              flexWrap: "wrap",
            }}
          >
            {" "}
            <Box
              sx={{ border: "1px solid #ddd", p: 2, minWidth: 220, flex: 1 }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Choose a Seed Lot to Explore
              </Typography>

              <TextField
                fullWidth
                size="small"
                select
                value={selectedSeedLotId}
                onChange={(e) => setSelectedSeedLotId(e.target.value)}
                sx={{ width: 220 }}
              >
                <MenuItem value="all">All Seeds</MenuItem>
                {(seeds ?? []).flatMap((seed) =>
                  (seed.lots ?? [])
                    .filter((lot) => lot.archivedAt === null)
                    .map((lot) => (
                      <MenuItem key={lot.id} value={lot.id.toString()}>
                        {seed.name} - {lot.number}
                      </MenuItem>
                    )),
                )}{" "}
              </TextField>
            </Box>
            <Box
              sx={{
                border: "1px solid #ddd",
                p: 2,
                minWidth: 220,
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Button
                variant="contained"
                sx={{ minWidth: 220, fontSize: 22, mt: 1 }}
                disabled={selectedSeedLotId === "all"}
                onClick={() => {
                  setEditingSeedAuditTransaction(null);
                  setSeedAuditCountedStock("");
                  setSeedAuditNotes("");
                  setSeedAuditOpen(true);
                }}
              >
                Run Audit
              </Button>
            </Box>
          </Box>
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Seed Lot Details
            </Typography>

            <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              <Typography>
                <strong>Date Received:</strong>{" "}
                {selectedLot?.lot.dateReceived
                  ? DateTime.fromISO(
                      selectedLot.lot.dateReceived,
                    ).toLocaleString(DateTime.DATE_FULL)
                  : "--"}
              </Typography>

              <Typography>
                <strong>Invoice #:</strong>{" "}
                {selectedLot?.lot.invoiceNumber || "--"}
              </Typography>

              <Typography>
                <strong>Qty Received:</strong>{" "}
                {selectedLot?.lot.qtyReceivedKg
                  ? `${selectedLot.lot.qtyReceivedKg} kg`
                  : "--"}{" "}
              </Typography>

              <Typography>
                <strong>Lot Cost:</strong>{" "}
                {selectedLot?.lot.costOfLot != null
                  ? formatCurrency(selectedLot.lot.costOfLot, grower.CountryISO)
                  : "--"}
              </Typography>

              <Typography>
                <strong>Lot Cost/100 g:</strong>{" "}
                {selectedLot?.lot.costOfLot != null &&
                selectedLot?.lot.qtyReceivedKg
                  ? formatCurrency(
                      selectedLot.lot.costOfLot /
                        (selectedLot.lot.qtyReceivedKg * 10),
                      grower.CountryISO,
                    )
                  : "--"}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 2, mb: 3, mt: 3, flexWrap: "wrap" }}>
            <Box sx={{ border: "1px solid #ddd", p: 2, minWidth: 180 }}>
              <Typography variant="subtitle2">Opening Stock</Typography>
              <Typography variant="h5">
                {openingStockKg.toFixed(3)} kg
              </Typography>
            </Box>
            <Box sx={{ border: "1px solid #ddd", p: 2, minWidth: 180 }}>
              <Typography variant="subtitle2">
                Seed Used + Adjustments
              </Typography>
              <Typography variant="h5">
                {seedUsedKg.toFixed(3)} kg
              </Typography>{" "}
            </Box>
            <Box sx={{ border: "1px solid #ddd", p: 2, minWidth: 180 }}>
              <Typography variant="subtitle2">Current Stock</Typography>
              <Typography variant="h5">
                {currentStockKg.toFixed(3)} kg
              </Typography>{" "}
            </Box>
            <Box sx={{ border: "1px solid #ddd", p: 2, minWidth: 180 }}>
              <Typography variant="subtitle2">Inventory Value</Typography>
              <Typography variant="h5">
                {formatCurrency(seedInventoryValue, grower.CountryISO)}
              </Typography>{" "}
            </Box>
          </Box>

          <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
            Data Tables Date Range:
          </Typography>

          <FormControl variant="standard" sx={{ minWidth: 120, mb: 4 }}>
            <Select
              variant="standard"
              value={dateRange}
              onChange={handleChangeDateRange}
            >
              <MenuItem value={DateRange.Last30Days}>Last 30 days</MenuItem>
              <MenuItem value={DateRange.ThisMonth}>This Month</MenuItem>
              <MenuItem value={DateRange.LastMonth}>Last Month</MenuItem>
              <MenuItem value={DateRange.ThisYear}>This Year</MenuItem>
              <MenuItem value={DateRange.Custom}>Custom</MenuItem>
            </Select>
          </FormControl>

          {dateRange === DateRange.Custom && (
            <>
              <MobileDatePicker
                closeOnSelect
                value={fromDate}
                onChange={(value) => setFromDate(value)}
                label="From"
                format="MMM d, y"
                sx={{ ml: 2 }}
              />

              <MobileDatePicker
                closeOnSelect
                value={toDate}
                onChange={(value) => setToDate(value)}
                label="To"
                format="MMM d, y"
                sx={{ ml: 2 }}
              />
            </>
          )}

          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ bgcolor: "#cccccc" }}
            >
              <Typography variant="h6">Seed Use Records</Typography>
            </AccordionSummary>

            <AccordionDetails>
              <Box sx={{ height: 400 }}>
                <StripedDataGrid
                  storageKey="stock-audit-seed-use"
                  rows={filteredSeedUseRecords}
                  columns={seedUseColumns}
                  getRowId={(row) =>
                    `${row.seedLot}-${row.dateUsed}-${row.cropBatchNumber}-${row.totalUsedKg}`
                  }
                  initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: 10,
                      },
                    },
                    filter: undefined,
                    sorting: {
                      sortModel: [
                        {
                          field: "id",
                          sort: "desc",
                        },
                      ],
                    },
                  }}
                  pageSizeOptions={[5, 10, 25, 50]}
                  slots={{
                    toolbar: GridToolbar,
                  }}
                  showToolbar
                  slotProps={{
                    toolbar: {
                      showQuickFilter: true,
                      csvOptions: {
                        allColumns: true,
                      },
                    },
                  }}
                  disableRowSelectionOnClick
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ bgcolor: "#cccccc" }}
            >
              <Typography variant="h6">Seed Lots</Typography>
            </AccordionSummary>

            <AccordionDetails>
              <Box sx={{ height: 400 }}>
                <StripedDataGrid
                  storageKey="stock-audit-seed-lots"
                  rows={filteredSeedInputRows}
                  columns={seedInputColumns}
                  disableRowSelectionOnClick
                  slots={{
                    toolbar: GridToolbar,
                  }}
                  showToolbar
                  slotProps={{
                    toolbar: {
                      showQuickFilter: true,
                      csvOptions: {
                        allColumns: true,
                      },
                    },
                  }}
                  initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: 10,
                      },
                    },
                    filter: undefined,
                    sorting: {
                      sortModel: [
                        {
                          field: "id",
                          sort: "desc",
                        },
                      ],
                    },
                  }}
                  pageSizeOptions={[5, 10, 25, 50]}
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ bgcolor: "#cccccc" }}
            >
              <Typography variant="h6">Seed Stock History</Typography>
            </AccordionSummary>

            <AccordionDetails>
              <Box sx={{ height: 400 }}>
                <StripedDataGrid
                  storageKey="stock-audit-seed-stock-history"
                  rows={filteredSeedStockHistoryRows}
                  columns={seedStockHistoryColumns}
                  disableRowSelectionOnClick
                  slots={{
                    toolbar: GridToolbar,
                  }}
                  showToolbar
                  slotProps={{
                    toolbar: {
                      showQuickFilter: true,
                      csvOptions: {
                        allColumns: true,
                      },
                    },
                  }}
                  initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: 10,
                      },
                    },
                    filter: undefined,
                    sorting: {
                      sortModel: [
                        {
                          field: "id",
                          sort: "desc",
                        },
                      ],
                    },
                  }}
                  pageSizeOptions={[5, 10, 25, 50]}
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}

      {selectedTab === 1 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Package Lots
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "stretch",
              flexWrap: "wrap",
            }}
          >
            {" "}
            <Box
              sx={{ border: "1px solid #ddd", p: 2, minWidth: 220, flex: 1 }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Choose a Package Lot to Explore
              </Typography>

              <TextField
                fullWidth
                size="small"
                select
                value={selectedPkgLotId}
                onChange={(e) => setSelectedPkgLotId(e.target.value)}
                sx={{ width: 220 }}
              >
                <MenuItem value="all">All Packages</MenuItem>
                {activePkgLots.map(({ pkg, lot }) => (
                  <MenuItem key={lot.id} value={lot.id.toString()}>
                    {pkg.name} - {lot.number}
                  </MenuItem>
                ))}{" "}
              </TextField>
            </Box>
            <Box
              sx={{
                border: "1px solid #ddd",
                p: 2,
                minWidth: 220,
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Button
                variant="contained"
                sx={{ minWidth: 220, fontSize: 22, mt: 1 }}
                disabled={selectedPkgLotId === "all"}
                onClick={() => {
                  setEditingPkgAuditTransaction(null);
                  setPkgAuditCountedStock("");
                  setPkgAuditNotes("");
                  setPkgAuditOpen(true);
                }}
              >
                Run Audit
              </Button>{" "}
            </Box>
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Package Lot Details
            </Typography>

            <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              <Typography>
                <strong>Date Received:</strong>{" "}
                {selectedPkgLot?.lot.dateReceived
                  ? DateTime.fromISO(
                      selectedPkgLot.lot.dateReceived,
                    ).toLocaleString(DateTime.DATE_FULL)
                  : "--"}
              </Typography>

              <Typography>
                <strong>Invoice #:</strong>{" "}
                {selectedPkgLot?.lot.invoiceNumber || "--"}
              </Typography>

              <Typography>
                <strong>Qty Received:</strong>{" "}
                {selectedPkgLot?.lot.qtyReceived != null
                  ? selectedPkgLot.lot.qtyReceived.toFixed(0)
                  : "--"}
              </Typography>

              <Typography>
                <strong>Lot Cost:</strong>{" "}
                {selectedPkgLot?.lot.costOfLot != null
                  ? formatCurrency(
                      selectedPkgLot.lot.costOfLot,
                      grower.CountryISO,
                    )
                  : "--"}
              </Typography>

              <Typography>
                <strong>Lot Cost/package:</strong>{" "}
                {selectedPkgLot?.lot.costOfLot != null &&
                selectedPkgLot?.lot.qtyReceived
                  ? formatCurrency(
                      selectedPkgLot.lot.costOfLot /
                        selectedPkgLot.lot.qtyReceived,
                      grower.CountryISO,
                    )
                  : "--"}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 2, mb: 3, mt: 3, flexWrap: "wrap" }}>
            <Box sx={{ border: "1px solid #ddd", p: 2, minWidth: 180 }}>
              <Typography variant="subtitle2">Opening Stock</Typography>
              <Typography variant="h5">{pkgOpeningStock.toFixed(0)}</Typography>
            </Box>

            <Box sx={{ border: "1px solid #ddd", p: 2, minWidth: 180 }}>
              <Typography variant="subtitle2">
                Packages Used + Adjustments
              </Typography>
              <Typography variant="h5">{pkgUsed.toFixed(0)}</Typography>
            </Box>

            <Box sx={{ border: "1px solid #ddd", p: 2, minWidth: 180 }}>
              <Typography variant="subtitle2">Current Stock</Typography>
              <Typography variant="h5">{pkgCurrentStock.toFixed(0)}</Typography>
            </Box>

            <Box sx={{ border: "1px solid #ddd", p: 2, minWidth: 180 }}>
              <Typography variant="subtitle2">Inventory Value</Typography>
              <Typography variant="h5">
                {formatCurrency(pkgInventoryValue, grower.CountryISO)}
              </Typography>
            </Box>
          </Box>

          <Typography variant="h6" sx={{ mt: 4 }}>
            Data Table Date Range:
          </Typography>

          <Box
            sx={{ display: "flex", gap: 2, mb: 4, mt: 2, alignItems: "center" }}
          >
            <FormControl variant="standard" sx={{ minWidth: 120 }}>
              <Select
                variant="standard"
                value={dateRange}
                onChange={handleChangeDateRange}
              >
                <MenuItem value={DateRange.Last30Days}>Last 30 days</MenuItem>
                <MenuItem value={DateRange.ThisMonth}>This Month</MenuItem>
                <MenuItem value={DateRange.LastMonth}>Last Month</MenuItem>
                <MenuItem value={DateRange.ThisYear}>This Year</MenuItem>
                <MenuItem value={DateRange.Custom}>Custom</MenuItem>
              </Select>
            </FormControl>

            {dateRange === DateRange.Custom && (
              <>
                <MobileDatePicker
                  closeOnSelect
                  value={fromDate}
                  onChange={(value) => setFromDate(value)}
                  label="From"
                  format="MMM d, y"
                  sx={{ ml: 2 }}
                />

                <MobileDatePicker
                  closeOnSelect
                  value={toDate}
                  onChange={(value) => setToDate(value)}
                  label="To"
                  format="MMM d, y"
                  sx={{ ml: 2 }}
                />
              </>
            )}
          </Box>

          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ bgcolor: "#cccccc" }}
            >
              <Typography variant="h6">Package Use Records</Typography>
            </AccordionSummary>

            <AccordionDetails>
              <Box sx={{ height: 400 }}>
                <StripedDataGrid
                  storageKey="stock-audit-package-use"
                  rows={filteredPkgUseRecords}
                  columns={pkgUseColumns}
                  disableRowSelectionOnClick
                  getRowId={(row) => row.id}
                  showToolbar
                  slots={{
                    toolbar: GridToolbar,
                  }}
                  slotProps={{
                    toolbar: {
                      showQuickFilter: true,
                      csvOptions: {
                        allColumns: true,
                      },
                    },
                  }}
                  initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: 10,
                      },
                    },
                    filter: undefined,
                    sorting: {
                      sortModel: [
                        {
                          field: "id",
                          sort: "desc",
                        },
                      ],
                    },
                  }}
                  pageSizeOptions={[5, 10, 25, 50]}
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ bgcolor: "#cccccc" }}
            >
              <Typography variant="h6">Package Lots</Typography>
            </AccordionSummary>

            <AccordionDetails>
              <Box sx={{ height: 400 }}>
                <StripedDataGrid
                  storageKey="stock-audit-package-lots"
                  rows={filteredPkgInputRows}
                  columns={pkgInputColumns}
                  disableRowSelectionOnClick
                  slots={{
                    toolbar: GridToolbar,
                  }}
                  showToolbar
                  slotProps={{
                    toolbar: {
                      showQuickFilter: true,
                      csvOptions: {
                        allColumns: true,
                      },
                    },
                  }}
                  initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: 10,
                      },
                    },
                    filter: undefined,
                    sorting: {
                      sortModel: [
                        {
                          field: "id",
                          sort: "desc",
                        },
                      ],
                    },
                  }}
                  pageSizeOptions={[5, 10, 25, 50]}
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ bgcolor: "#cccccc" }}
            >
              <Typography variant="h6">Package Stock History</Typography>
            </AccordionSummary>

            <AccordionDetails>
              <Box sx={{ height: 400 }}>
                <StripedDataGrid
                  storageKey="stock-audit-package-stock-history"
                  rows={filteredPkgStockHistoryRows}
                  columns={pkgStockHistoryColumns}
                  disableRowSelectionOnClick
                  getRowId={(row) => row.id}
                  slots={{
                    toolbar: GridToolbar,
                  }}
                  showToolbar
                  slotProps={{
                    toolbar: {
                      showQuickFilter: true,
                      csvOptions: {
                        allColumns: true,
                      },
                    },
                  }}
                  initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: 10,
                      },
                    },
                    filter: undefined,
                    sorting: {
                      sortModel: [
                        {
                          field: "id",
                          sort: "desc",
                        },
                      ],
                    },
                  }}
                  pageSizeOptions={[5, 10, 25, 50]}
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}

      {selectedTab === 2 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Soil Lots
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "stretch",
              flexWrap: "wrap",
            }}
          >
            <Box
              sx={{ border: "1px solid #ddd", p: 2, minWidth: 220, flex: 1 }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Choose a Soil Lot to Explore
              </Typography>

              <TextField
                fullWidth
                size="small"
                select
                value="all"
                sx={{ width: 220 }}
              >
                <MenuItem value="all">All Soil</MenuItem>
              </TextField>
            </Box>

            <Box
              sx={{
                border: "1px solid #ddd",
                p: 2,
                minWidth: 220,
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Button
                variant="contained"
                sx={{ minWidth: 220, fontSize: 22, mt: 1 }}
              >
                Run Audit
              </Button>
            </Box>
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Soil Lot Details
            </Typography>

            <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              <Typography>
                <strong>Date Received:</strong> --
              </Typography>

              <Typography>
                <strong>Invoice #:</strong> --
              </Typography>

              <Typography>
                <strong>Qty Received:</strong> --
              </Typography>

              <Typography>
                <strong>Lot Cost:</strong> --
              </Typography>

              <Typography>
                <strong>Lot Cost/unit:</strong> --
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 2, mb: 3, mt: 3, flexWrap: "wrap" }}>
            <Box sx={{ border: "1px solid #ddd", p: 2, minWidth: 180 }}>
              <Typography variant="subtitle2">Opening Stock</Typography>
              <Typography variant="h5">0</Typography>
            </Box>

            <Box sx={{ border: "1px solid #ddd", p: 2, minWidth: 180 }}>
              <Typography variant="subtitle2">Soil Used</Typography>
              <Typography variant="h5">0</Typography>
            </Box>

            <Box sx={{ border: "1px solid #ddd", p: 2, minWidth: 180 }}>
              <Typography variant="subtitle2">Current Stock</Typography>
              <Typography variant="h5">0</Typography>
            </Box>

            <Box sx={{ border: "1px solid #ddd", p: 2, minWidth: 180 }}>
              <Typography variant="subtitle2">Inventory Value</Typography>
              <Typography variant="h5">
                {formatCurrency(0, grower.CountryISO)}
              </Typography>
            </Box>
          </Box>

          <Typography variant="h6" sx={{ mt: 4 }}>
            Data Table Date Range:
          </Typography>

          <Box
            sx={{ display: "flex", gap: 2, mb: 4, mt: 2, alignItems: "center" }}
          >
            <FormControl variant="standard" sx={{ minWidth: 120 }}>
              <Select
                variant="standard"
                value={dateRange}
                onChange={handleChangeDateRange}
              >
                <MenuItem value={DateRange.Last30Days}>Last 30 days</MenuItem>
                <MenuItem value={DateRange.ThisMonth}>This Month</MenuItem>
                <MenuItem value={DateRange.LastMonth}>Last Month</MenuItem>
                <MenuItem value={DateRange.ThisYear}>This Year</MenuItem>
                <MenuItem value={DateRange.Custom}>Custom</MenuItem>
              </Select>
            </FormControl>

            {dateRange === DateRange.Custom && (
              <>
                <MobileDatePicker
                  closeOnSelect
                  value={fromDate}
                  onChange={(value) => setFromDate(value)}
                  label="From"
                  format="MMM d, y"
                  sx={{ ml: 2 }}
                />

                <MobileDatePicker
                  closeOnSelect
                  value={toDate}
                  onChange={(value) => setToDate(value)}
                  label="To"
                  format="MMM d, y"
                  sx={{ ml: 2 }}
                />
              </>
            )}
          </Box>

          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ bgcolor: "#cccccc" }}
            >
              <Typography variant="h6">Soil Use Records</Typography>
            </AccordionSummary>

            <AccordionDetails>
              <Box sx={{ height: 400 }}>
                <StripedDataGrid
                  storageKey="stock-audit-soil-use"
                  rows={soilUseRows}
                  columns={soilUseColumns}
                  disableRowSelectionOnClick
                  slots={{
                    toolbar: GridToolbar,
                  }}
                  showToolbar
                  slotProps={{
                    toolbar: {
                      showQuickFilter: true,
                      csvOptions: {
                        allColumns: true,
                      },
                    },
                  }}
                  initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: 10,
                      },
                    },
                    filter: undefined,
                    sorting: {
                      sortModel: [
                        {
                          field: "id",
                          sort: "desc",
                        },
                      ],
                    },
                  }}
                  pageSizeOptions={[5, 10, 25, 50]}
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ bgcolor: "#cccccc" }}
            >
              <Typography variant="h6">Soil Lots</Typography>
            </AccordionSummary>

            <AccordionDetails>
              <Box sx={{ height: 400 }}>
                <StripedDataGrid
                  storageKey="stock-audit-soil-lots"
                  rows={soilInputRows}
                  columns={soilInputColumns}
                  disableRowSelectionOnClick
                  slots={{
                    toolbar: GridToolbar,
                  }}
                  showToolbar
                  slotProps={{
                    toolbar: {
                      showQuickFilter: true,
                      csvOptions: {
                        allColumns: true,
                      },
                    },
                  }}
                  initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: 10,
                      },
                    },
                    filter: undefined,
                    sorting: {
                      sortModel: [
                        {
                          field: "id",
                          sort: "desc",
                        },
                      ],
                    },
                  }}
                  pageSizeOptions={[5, 10, 25, 50]}
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ bgcolor: "#cccccc" }}
            >
              <Typography variant="h6">Soil Stock History</Typography>
            </AccordionSummary>

            <AccordionDetails>
              <Box sx={{ height: 400 }}>
                <StripedDataGrid
                  storageKey="stock-audit-soil-stock-history"
                  rows={soilStockHistoryRows}
                  columns={soilStockHistoryColumns}
                  disableRowSelectionOnClick
                  slots={{
                    toolbar: GridToolbar,
                  }}
                  showToolbar
                  slotProps={{
                    toolbar: {
                      showQuickFilter: true,
                      csvOptions: {
                        allColumns: true,
                      },
                    },
                  }}
                  initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: 10,
                      },
                    },
                    filter: undefined,
                    sorting: {
                      sortModel: [
                        {
                          field: "id",
                          sort: "desc",
                        },
                      ],
                    },
                  }}
                  pageSizeOptions={[5, 10, 25, 50]}
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}

      <Dialog
        open={seedAuditOpen}
        onClose={() => setSeedAuditOpen(false)}
        fullWidth
      >
        <DialogTitle>Run Seed Stock Audit</DialogTitle>

        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Count the current stock, enter the counted amount, and add notes if
            needed.
          </Typography>

          <Typography sx={{ mb: 2 }}>
            <strong>Selected Lot:</strong>{" "}
            {selectedLot
              ? `${selectedLot.seed.name} - ${selectedLot.lot.number}`
              : "All Seeds"}
          </Typography>

          <Typography sx={{ mb: 2 }}>
            <strong>Expected Stock:</strong>{" "}
            {seedAuditExpectedStockKg.toFixed(3)} kg
          </Typography>

          <TextField
            fullWidth
            label="Counted Stock (kg)"
            value={seedAuditCountedStock}
            onChange={(e) => setSeedAuditCountedStock(e.target.value)}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Audit Notes"
            value={seedAuditNotes}
            onChange={(e) => setSeedAuditNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSeedAuditOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => setSeedAuditConfirmOpen(true)}
          >
            Apply Audit
          </Button>{" "}
        </DialogActions>
      </Dialog>
      <Dialog
        open={seedAuditConfirmOpen}
        onClose={() => setSeedAuditConfirmOpen(false)}
        fullWidth
      >
        <DialogTitle>Add adjustment to balance stock?</DialogTitle>

        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            This will update the selected seed lot stock and add an audit
            adjustment to Seed Stock History.
          </Typography>

          <Typography sx={{ mb: 1 }}>
            <strong>Expected Stock:</strong>{" "}
            {seedAuditExpectedStockKg.toFixed(3)} kg{" "}
          </Typography>

          <Typography sx={{ mb: 1 }}>
            <strong>Counted Stock:</strong>{" "}
            {Number(seedAuditCountedStock).toFixed(3)} kg
          </Typography>

          <Typography sx={{ mb: 2 }}>
            <strong>Adjustment:</strong>{" "}
            {(Number(seedAuditCountedStock) - seedAuditExpectedStockKg).toFixed(
              3,
            )}{" "}
            kg
          </Typography>

          <Typography>
            Add adjustment to balance stock? (Recommended).
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setSeedAuditConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (!selectedLot && !editingSeedAuditTransaction) {
                return;
              }

              const saveSeedAudit = editingSeedAuditTransaction
                ? seedsApi.updateAudit(editingSeedAuditTransaction.id, {
                    countedStockKg: Number(seedAuditCountedStock),
                    notes: seedAuditNotes,
                  })
                : seedsApi.runAudit({
                    seedLotId: selectedLot!.lot.id,
                    countedStockKg: Number(seedAuditCountedStock),
                    notes: seedAuditNotes,
                  });

              saveSeedAudit
                .then(() => {
                  return Promise.all([
                    seedsApi.getAll().then((data) => setSeeds(data ?? [])),
                    seedsApi
                      .getUseRecords()
                      .then((data) => setSeedUseRecords(data ?? [])),
                    seedsApi
                      .getStockHistory()
                      .then((data) => setSeedStockHistoryRows(data ?? [])),
                  ]);
                })
                .then(() => {
                  setSeedAuditConfirmOpen(false);
                  setSeedAuditOpen(false);
                  setSeedAuditCountedStock("");
                  setSeedAuditNotes("");
                  setEditingSeedAuditTransaction(null);
                });
            }}
          >
            Confirm Adjustment
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={pkgAuditOpen}
        onClose={() => setPkgAuditOpen(false)}
        fullWidth
      >
        <DialogTitle>Run Package Stock Audit</DialogTitle>

        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Count the current stock, enter the counted amount, and add notes if
            needed.
          </Typography>

          <Typography sx={{ mb: 2 }}>
            <strong>Expected Stock:</strong> {pkgCurrentStock.toFixed(0)}
          </Typography>

          <TextField
            fullWidth
            label="Counted Stock"
            value={pkgAuditCountedStock}
            onChange={(e) => setPkgAuditCountedStock(e.target.value)}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Audit Notes"
            value={pkgAuditNotes}
            onChange={(e) => setPkgAuditNotes(e.target.value)}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setPkgAuditOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => setPkgAuditConfirmOpen(true)}
          >
            Apply Audit
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={pkgAuditConfirmOpen}
        onClose={() => setPkgAuditConfirmOpen(false)}
        fullWidth
      >
        <DialogTitle>Add adjustment to balance stock?</DialogTitle>

        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            This will update the selected package lot stock and add an audit
            adjustment to Package Stock History.
          </Typography>

          <Typography sx={{ mb: 1 }}>
            <strong>Expected Stock:</strong> {pkgCurrentStock.toFixed(0)}
          </Typography>

          <Typography sx={{ mb: 1 }}>
            <strong>Counted Stock:</strong>{" "}
            {Number(pkgAuditCountedStock).toFixed(0)}
          </Typography>

          <Typography sx={{ mb: 2 }}>
            <strong>Adjustment:</strong>{" "}
            {(Number(pkgAuditCountedStock) - pkgCurrentStock).toFixed(0)}
          </Typography>

          <Typography>
            Add adjustment to balance stock? (Recommended).
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setPkgAuditConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (!selectedPkgLot && !editingPkgAuditTransaction) {
                return;
              }

              const savePkgAudit = editingPkgAuditTransaction
                ? pkgLotsApi.updateAudit(editingPkgAuditTransaction.id, {
                    countedStock: Number(pkgAuditCountedStock),
                    notes: pkgAuditNotes,
                  })
                : pkgLotsApi.runAudit({
                    pkgLotId: selectedPkgLot!.lot.id,
                    countedStock: Number(pkgAuditCountedStock),
                    notes: pkgAuditNotes,
                  });

              savePkgAudit
                .then(() => {
                  return Promise.all([
                    pkgsApi.getAll().then((data) => setPkgs(data ?? [])),
                    pkgLotsApi
                      .getUseRecords()
                      .then((data) => setPkgUseRecords(data ?? [])),
                    pkgLotsApi
                      .getStockSummary()
                      .then((data) => setPkgStockSummary(data ?? [])),
                    pkgLotsApi
                      .getStockHistory()
                      .then((data) => setPkgStockHistoryRows(data ?? [])),
                  ]);
                })
                .then(() => {
                  setPkgAuditConfirmOpen(false);
                  setPkgAuditOpen(false);
                  setPkgAuditCountedStock("");
                  setPkgAuditNotes("");
                  setEditingPkgAuditTransaction(null);
                });
            }}
          >
            Confirm Adjustment
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
